import React from 'react';

import { Trans, useTranslation } from 'react-i18next';

import arrowIcon from 'assets/img/account/mobile/right_arrow.svg';
import mobileThankYouImg from 'assets/img/thank-you-mobile.png';
import thankYouImg from 'assets/img/thank-you.png';
import { useHeaderInteractor } from 'modules/header/interactor';

import { PrimaryButton } from 'components/buttons/primary-button';
import { LoadingAnimation } from 'components/loading-animation';
import LocalizedLink from 'components/localized-link';

import type { DropDownList } from 'utils/links';
import { getConversionLinks, getLinksAdvancedTools, getLinksCompress, getLinksEditPdf } from 'utils/links';

import { InternalFileType } from 'ts/types/file';

interface ThankYouBlockProps {
  isDownloading: boolean;
  handleDownloadFile: () => void;
  handleClickDashboard: () => void;
}

export const ThankYouBlock = ({ isDownloading, handleDownloadFile, handleClickDashboard }: ThankYouBlockProps) => {
  const interactor = useHeaderInteractor();
  const { t } = useTranslation();
  const generalLinks = [
    ...Object.values(getConversionLinks(t).links),
    ...Object.values(getLinksAdvancedTools(t).links),
    ...Object.values(getLinksCompress(t).links),
    ...Object.values(getLinksEditPdf(t).links)
  ];

  const renderList = React.useCallback(
    (generalLinks: DropDownList['links']) => {
      const chunkSize = 3;
      const rows = [];

      for (let i = 0; i < generalLinks.length; i += chunkSize) {
        const chunk = generalLinks.slice(i, i + chunkSize);
        rows.push(
          <div
            className='flex flex-col mobile:flex-row justify-center gap-4 my-2 w-full'
            key={i}
          >
            {chunk.map((link, index) => (
              <LocalizedLink
                href={link.href}
                key={index}
                className='flex flex-row items-center w-full mobile:w-[240px] bg-white rounded-xl shadow-sm px-4 py-3 hover:shadow-md transition-shadow duration-200'
                onClick={() =>
                  interactor.convertDropDown.onClickHeaderFeature(
                    link.from,
                    link.to || InternalFileType.PDF,
                    link.href.slice(1)
                  )
                }
              >
                <div
                  className='flex items-center justify-center w-[32px] h-[32px] rounded-md mr-3'
                  style={{ backgroundColor: '#FEE2E2' }}
                >
                  <img
                    src={link.img}
                    alt={link.label}
                    className='w-full h-full object-contain min-w-8'
                  />
                </div>

                <span className='flex-grow text-[16px] font-medium text-black'>{link.label}</span>

                <img
                  src={arrowIcon}
                  alt='arrow'
                  className='w-[14px] h-[14px]'
                />
              </LocalizedLink>
            ))}
          </div>
        );
      }

      return rows;
    },
    [interactor.convertDropDown]
  );

  return (
    <div className='flex flex-col tablet:flex-row items-stretch w-full max-w-[1140px] rounded-3xl shadow-xl mt-10 tablet:mt-20 bg-white overflow-hidden'>
      <div className='order-1 tablet:order-2 w-full tablet:w-[410px]'>
        <div className='flex justify-center items-center tablet:hidden h-[160px]'>
          <img
            src={mobileThankYouImg}
            alt='thank-you-block-mobile'
            className='h-full w-auto object-contain'
          />
        </div>

        <div className='hidden tablet:block w-full h-full'>
          <img
            src={thankYouImg}
            alt='thank-you-block-desktop'
            className='w-full h-full object-cover'
          />
        </div>
      </div>

      <div className='order-2 tablet:order-1 flex flex-col justify-center w-full max-w-[740px] px-4 py-6 tablet:px-10 tablet:py-12'>
        <h2 className='text-[24px] tablet:text-[32px] font-extrabold leading-[32px] tablet:leading-[42px] text-black mb-4'>
          {t('payment_success_page.thank_you_title')}
        </h2>

        <p className='text-[16px] tablet:text-[18px] text-[#4B4B4B] leading-[24px] tablet:leading-[26px] mb-6 tablet:mb-8'>
          <Trans i18nKey='payment_success_page.purchase_title' />
        </p>

        <div className='flex flex-row gap-4'>
          <PrimaryButton
            onClick={handleDownloadFile}
            disabled={isDownloading}
            className='bg-[#5F32E5] hover:bg-[#4a25b8] text-white font-medium text-[16px] rounded-xl px-6 py-3 w-full tablet:max-w-[240px] mb-10'
            dataTestId='payment-success-download-button'
          >
            {isDownloading ? (
              <div className='flex justify-center items-center font-bold'>
                <LoadingAnimation
                  currentState='loading'
                  className='!w-5 mr-2'
                />
                {t('payment_page.downloading_file')}
              </div>
            ) : (
              <>{t('payment_success_page.download_button_new')}</>
            )}
          </PrimaryButton>
          <PrimaryButton
            onClick={handleClickDashboard}
            dataTestId='payment-success-all-doc-button'
            className='sm:w-fit rounded-xl text-base border-[2px] border-solid border-[#1D1D1D] !bg-white !text-[#1D1D1D] hover:!bg-[#1D1D1D] hover:!text-[white] font-medium text-[16px] px-6 py-3 w-full tablet:max-w-[240px] mb-10'
          >
            {t('payment_success_page.all_doc_button')}
          </PrimaryButton>
        </div>

        <div className='bg-[#F6F6F6] p-4 rounded-xl w-full'>{renderList(generalLinks)}</div>
      </div>
    </div>
  );
};
